import React, { useState } from 'react'
import type { MessageDescriptor } from 'react-intl'
import { useIntl } from 'react-intl'
import { useData } from 'vike-react/useData'

import type { SanityTextContentProps } from '@slite/components'
import {
  ArticleLayout,
  SanityTextRenderer,
  youtubeEmbedUrl,
} from '@slite/components'

import { useLeadGateAccess } from '../../../components/LeadGate/useLeadGateAccess'
import { NAVBAR_HEIGHT } from '../../../components/Navbar/NavbarSection'
import { Section } from '../../../components/Section'
import { SectionLastCTA } from '../../../components/SectionLastCTA'
import { SlitePage } from '../../../components/SlitePage'
import { useBanner } from '../../../components/useBanner'
import { useRoutes } from '../../../routes'
import { estimateReadingTimeMinutes } from '../../../utils/readingTime'
import { getSliteWebsiteConfig } from '../../data'
import type { Data } from './+data'
import { WebinarGateModal } from './WebinarGateModal'

// The values of the `speakersLabel` field in Sanity.
const SPEAKERS_LABELS: Record<string, MessageDescriptor> = {
  'written-by': {
    id: 'webinars.show.speakers.writtenBy',
    defaultMessage: 'Written by',
  },
  collaboration: {
    id: 'webinars.show.speakers.collaboration',
    defaultMessage: 'Collaboration',
  },
  'talk-with': {
    id: 'webinars.show.speakers.talkWith',
    defaultMessage: 'A talk with',
  },
}

export default function WebinarPage() {
  const { webinar, alternateHrefLangUrls } = useData<Data>()
  const intl = useIntl()
  const routes = useRoutes()
  const { navbarTopOffset } = useBanner()
  const gate = useLeadGateAccess(
    webinar.gated
      ? { source: 'webinar-gate', contentSlug: webinar.slug.current }
      : undefined
  )
  const [autoPlayVideo, setAutoPlayVideo] = useState(false)
  const isGateOpen = webinar.gated && gate.isResolved && !gate.hasAccess

  const unlockAndPlay = () => {
    gate.grantAccess()
    setAutoPlayVideo(true)
  }

  const pageUrl = routes.webinars.detail({ slug: webinar.slug.current })
  const content = webinar.content as SanityTextContentProps
  const readingTime = estimateReadingTimeMinutes(content)
  const speakersLabel = intl.formatMessage(
    SPEAKERS_LABELS[webinar.speakersLabel] ?? SPEAKERS_LABELS['talk-with']
  )

  return (
    <SlitePage
      title={webinar.title}
      description={webinar.metaDescription ?? undefined}
      ogTitle={webinar.openGraphTitle ?? webinar.title}
      ogDescription={webinar.openGraphDescription ?? undefined}
      ogImage={webinar.openGraphImage?.url ?? webinar.heroImage.url}
      canonicalUrl={pageUrl}
      alternateHrefLangUrls={alternateHrefLangUrls}
      background="moon-dust-lighter"
      additionalMetaTags={[{ name: 'og:type', content: 'article' }]}
    >
      <Section background="moon-dust-lighter" containerTopPadding={40}>
        <ArticleLayout
          title={webinar.title}
          date={intl.formatDate(webinar.date, { dateStyle: 'medium' })}
          dateTime={webinar.date}
          readingTime={intl.formatMessage(
            {
              id: 'webinars.show.readingTime',
              defaultMessage: '{minutes} min read',
            },
            { minutes: readingTime }
          )}
          category={intl.formatMessage({
            id: 'webinars.show.category',
            defaultMessage: 'Webinar',
          })}
          authors={webinar.speakers.map((speaker) => ({
            name: speaker.name,
            imageUrl: speaker.picture?.url,
            bio: speaker.bio as SanityTextContentProps | null,
          }))}
          authorsLabel={speakersLabel}
          backLabel={intl.formatMessage({
            id: 'webinars.show.allWebinars',
            defaultMessage: 'All webinars',
          })}
          backHref={routes.webinars.index()}
          ctaLabel={
            webinar.ctaLabel ??
            intl.formatMessage({
              id: 'webinars.show.cta',
              defaultMessage: 'Talk to our team',
            })
          }
          ctaHref={webinar.ctaLink ?? routes.bookDemo()}
          showCtaInSidebar
          heroImageUrl={webinar.heroImage.url}
          heroImageAlt={webinar.heroImage.alt ?? webinar.title}
          autoPlayVideo={autoPlayVideo}
          heroVideoEmbedUrl={
            webinar.youtubeVideoId
              ? youtubeEmbedUrl(webinar.youtubeVideoId)
              : undefined
          }
          watchButtonLabel={intl.formatMessage({
            id: 'webinars.show.watch',
            defaultMessage: 'Watch webinar',
          })}
          shareUrl={`${getSliteWebsiteConfig(intl).domain}${pageUrl}`}
          tocLabel={intl.formatMessage({
            id: 'webinars.show.toc',
            defaultMessage: 'Table of contents',
          })}
          shareLabel={intl.formatMessage({
            id: 'webinars.show.share',
            defaultMessage: 'Share',
          })}
          shareOnXLabel={intl.formatMessage({
            id: 'webinars.show.shareOnX',
            defaultMessage: 'Share on X',
          })}
          shareOnLinkedInLabel={intl.formatMessage({
            id: 'webinars.show.shareOnLinkedIn',
            defaultMessage: 'Share on LinkedIn',
          })}
          stickyTopOffset={NAVBAR_HEIGHT + navbarTopOffset}
        >
          <SanityTextRenderer variant="website" content={content} />
        </ArticleLayout>
      </Section>
      <SectionLastCTA background="moon-dust-lighter" />
      <WebinarGateModal
        isOpen={isGateOpen}
        slug={webinar.slug.current}
        title={webinar.title}
        onUnlocked={unlockAndPlay}
      />
    </SlitePage>
  )
}
